import * as XLSXModule from 'xlsx/dist/xlsx.full.min.js'

const XLSX = XLSXModule.default || XLSXModule

export const SHIPPING_IMPORT_HEADERS = ['订单ID', '订单编号', '快递名称', '快递单号']
export const SHIPPING_IMPORT_LIMIT = 500

function normalizeText(value) {
  if (value === null || value === undefined) {
    return ''
  }
  return String(value).replace(/^\uFEFF/, '').replace(/\u00A0/g, ' ').trim()
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve(event.target.result)
    reader.onerror = () => reject(new Error('文件读取失败，请重新选择文件'))
    reader.readAsArrayBuffer(file)
  })
}

function validateIdentifierCell(sheet, rowIndex, columnIndex, fieldLabel, value, errors) {
  const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })
  const cell = sheet[address]

  if (cell && cell.f) {
    errors.push(`${fieldLabel}不能是公式`)
  }

  if (cell && cell.t === 'n' && !Number.isSafeInteger(cell.v)) {
    errors.push(`${fieldLabel}超出 Excel 安全数字范围，请将单元格设为文本后重新填写`)
  }

  if (/^[+-]?\d+(?:\.\d+)?e[+-]?\d+$/i.test(value)) {
    errors.push(`${fieldLabel}不能使用科学计数法`)
  }
}

export async function parseShippingImportFile(file) {
  const arrayBuffer = await readFileAsArrayBuffer(file)
  let workbook

  try {
    workbook = XLSX.read(arrayBuffer, { type: 'array', cellFormula: true })
  } catch (error) {
    throw new Error('表格解析失败，请确认文件未损坏且格式为 xlsx、xls 或 csv')
  }

  const firstSheetName = workbook.SheetNames[0]
  if (!firstSheetName) {
    throw new Error('表格中没有可读取的工作表')
  }

  const sheet = workbook.Sheets[firstSheetName]
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: true,
  })

  if (!matrix.length) {
    throw new Error('表格内容为空')
  }

  const headerValues = matrix[0].map(normalizeText)
  const headerIndexes = {}
  SHIPPING_IMPORT_HEADERS.forEach((header) => {
    const matches = []
    headerValues.forEach((value, index) => {
      if (value === header) {
        matches.push(index)
      }
    })
    if (!matches.length && ['快递名称', '快递单号'].includes(header)) {
      throw new Error(`缺少必填列“${header}”，请使用导入模板`)
    }
    if (matches.length > 1) {
      throw new Error(`表头“${header}”重复，请保留一列`)
    }
    headerIndexes[header] = matches.length ? matches[0] : -1
  })

  if (headerIndexes['订单ID'] < 0 && headerIndexes['订单编号'] < 0) {
    throw new Error('“订单ID”和“订单编号”至少需要保留一列')
  }

  const rows = matrix.slice(1).map((values, index) => {
    const excelRow = index + 2
    const sheetRowIndex = index + 1
    const orderIdInput = headerIndexes['订单ID'] >= 0
      ? normalizeText(values[headerIndexes['订单ID']])
      : ''
    const orderNo = headerIndexes['订单编号'] >= 0
      ? normalizeText(values[headerIndexes['订单编号']])
      : ''
    const expressName = normalizeText(values[headerIndexes['快递名称']])
    const expressNo = normalizeText(values[headerIndexes['快递单号']])

    if (!orderIdInput && !orderNo && !expressName && !expressNo) {
      return null
    }

    const errors = []
    if (!orderIdInput && !orderNo) {
      errors.push('订单ID和订单编号至少填写一项')
    }
    if (!expressName) {
      errors.push('快递名称不能为空')
    }
    if (!expressNo && expressName !== '无需物流') {
      errors.push('快递单号不能为空')
    }

    if (orderIdInput) {
      validateIdentifierCell(
        sheet,
        sheetRowIndex,
        headerIndexes['订单ID'],
        '订单ID',
        orderIdInput,
        errors
      )
      if (!/^\d+$/.test(orderIdInput)) {
        errors.push('订单ID必须为整数')
      }
    }
    if (orderNo) {
      validateIdentifierCell(
        sheet,
        sheetRowIndex,
        headerIndexes['订单编号'],
        '订单编号',
        orderNo,
        errors
      )
    }
    if (expressNo) {
      validateIdentifierCell(
        sheet,
        sheetRowIndex,
        headerIndexes['快递单号'],
        '快递单号',
        expressNo,
        errors
      )
    }

    return {
      excelRow,
      orderIdInput,
      orderNo,
      expressName,
      expressNo,
      orderId: null,
      expressCode: null,
      status: errors.length ? 'invalid' : 'pending',
      reason: errors.join('；'),
      localErrors: errors,
    }
  }).filter(Boolean)

  if (!rows.length) {
    throw new Error('表格中没有可导入的数据')
  }
  if (rows.length > SHIPPING_IMPORT_LIMIT) {
    throw new Error(`单次最多导入 ${SHIPPING_IMPORT_LIMIT} 条，当前共 ${rows.length} 条，请拆分文件`)
  }

  const orderCounts = rows.reduce((counts, row) => {
    const matchKey = row.orderIdInput ? `id:${row.orderIdInput}` : `no:${row.orderNo}`
    if (matchKey !== 'no:') {
      counts[matchKey] = (counts[matchKey] || 0) + 1
    }
    return counts
  }, {})

  rows.forEach((row) => {
    const matchKey = row.orderIdInput ? `id:${row.orderIdInput}` : `no:${row.orderNo}`
    if (orderCounts[matchKey] > 1) {
      row.localErrors.push('同一表格中订单重复')
      row.status = 'invalid'
      row.reason = row.localErrors.join('；')
    }
  })

  return rows
}

function setTemplateTextCells(sheet) {
  for (let rowIndex = 1; rowIndex <= SHIPPING_IMPORT_LIMIT; rowIndex += 1) {
    for (let columnIndex = 0; columnIndex < SHIPPING_IMPORT_HEADERS.length; columnIndex += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })
      sheet[address] = { t: 's', v: '', z: '@' }
    }
  }
  sheet['!ref'] = `A1:D${SHIPPING_IMPORT_LIMIT + 1}`
}

export function downloadShippingTemplate(expressNames) {
  const importSheet = XLSX.utils.aoa_to_sheet([SHIPPING_IMPORT_HEADERS])
  setTemplateTextCells(importSheet)
  importSheet['!cols'] = [{ wch: 16 }, { wch: 28 }, { wch: 20 }, { wch: 28 }]
  importSheet['!autofilter'] = { ref: 'A1:D1' }

  const normalizedNames = Array.from(new Set(
    (expressNames || []).map(normalizeText).filter(Boolean)
  ))
  const instructionRows = [
    ['填写说明'],
    ['1. 请只在“批量发货导入”工作表填写数据。'],
    ['2. 订单ID和订单编号至少填写一项；两项都填时会以订单ID查询并校验订单编号。'],
    ['3. 订单ID、订单编号和快递单号请保持文本格式，不要使用科学计数法。'],
    ['4. 快递名称必须与下方名称完全一致。'],
    ['5. 填写“无需物流”时，快递单号可留空。'],
    [`6. 单次最多导入 ${SHIPPING_IMPORT_LIMIT} 条。`],
    [],
    ['可用快递名称'],
    ['无需物流'],
    ...normalizedNames.map((name) => [name]),
  ]
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructionRows)
  instructionSheet['!cols'] = [{ wch: 78 }]

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, importSheet, '批量发货导入')
  XLSX.utils.book_append_sheet(workbook, instructionSheet, '填写说明与快递名称')
  XLSX.writeFile(workbook, '订单批量发货导入模板.xlsx', {
    bookType: 'xlsx',
    compression: true,
  })
}

export function downloadShippingResults(rows) {
  const statusLabels = {
    invalid: '校验不通过',
    ready: '未处理',
    processing: '处理中',
    success: '成功',
    failed: '失败',
    unknown: '结果待确认',
    stopped: '未处理',
  }
  const exportRows = (rows || [])
    .filter((row) => ['invalid', 'failed', 'unknown', 'stopped'].includes(row.status))
    .map((row) => ({
      '原表行号': row.excelRow,
      '订单ID': row.orderIdInput || row.orderId || '',
      '订单编号': row.orderNo,
      '快递名称': row.expressName,
      '快递单号': row.expressNo,
      '处理结果': statusLabels[row.status] || row.status,
      '失败原因': row.reason || '',
    }))

  if (!exportRows.length) {
    return false
  }

  const sheet = XLSX.utils.json_to_sheet(exportRows)
  sheet['!cols'] = [
    { wch: 12 },
    { wch: 16 },
    { wch: 28 },
    { wch: 20 },
    { wch: 28 },
    { wch: 16 },
    { wch: 54 },
  ]
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, '未成功明细')
  const timestamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)
  XLSX.writeFile(workbook, `批量发货未成功明细-${timestamp}.xlsx`, {
    bookType: 'xlsx',
    compression: true,
  })
  return true
}
